import { useEffect, useMemo, useState } from 'react';

import { useAction } from '@playbooks/hooks';
import { FarIcons } from '@playbooks/icons';
import { SearchField } from '@playbooks/molecules/forms';
import { Spinner } from '@playbooks/ui/components/spinner';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';

import { AgentDesignFlow } from 'components/agent/agent-design-flow';
import { AgentDesignPalette } from 'components/agent/agent-design-palette';
import {
	ComposerActionFeedback,
	ComposerActionHeader,
	ComposerActionListSkeleton,
	ComposerActionRow,
	ComposerActionSection,
} from 'components/composer/composer-actions-components';
import { useProject, useStore, useWorkspace } from 'contexts';
import { useInit } from 'hooks';
import { FlowBody } from 'utils/flow';
import { projectResourcePayload } from 'utils/project-resources';

const ComposerActionsDesign = ({ type, disabled, preferences: draftPreferences, onChange, onPrev }) => {
	const [designs, setDesigns] = useState([]);
	const [query, setQuery] = useState('');
	const [flowOpen, setFlowOpen] = useState(false);
	const [flowView, setFlowView] = useState('select');
	const project = useProject();
	const store = useStore();
	const workspace = useWorkspace();
	const draftMode = type === 'app';
	const preferences = draftMode ? draftPreferences || {} : { designId: project.project?.design?.designId };
	const rootApi = type === 'project' ? `${project?.rootApi}/resources` : null;
	const defaultDesign = useMemo(() => designs.find(design => design.default), [designs]);
	const selectedId = preferences.designId || defaultDesign?.id || null;
	const compact = useMemo(() => designs.slice(0, 12), [designs]);
	const canManageResources = Boolean(workspace.workspace.access?.capabilities?.canManageProjectDefaults);

	const [fetchDesigns, loading, error] = useInit(async () => {
		const response = await store.query({
			url: workspace.workspace?.id ? `${workspace.rootApi}/designs` : '/agent-designs',
			params: { view: 'featured', pageSize: 12, ...(query.trim() ? { query: query.trim() } : {}) },
		});
		setDesigns(response.data || []);
	});

	useEffect(() => {
		const timeout = setTimeout(() => fetchDesigns(), query ? 200 : 0);
		return () => clearTimeout(timeout);
	}, [query]);

	const [onSelect, selectTask] = useAction(async design => {
		if (disabled) return;
		const designId = design.default || selectedId === design.id ? null : design.id;
		if (draftMode) {
			onChange?.({
				...preferences,
				designId,
				designResource: design.pendingResource || null,
			});
			return;
		}
		const response = await store.updateRecord({
			url: rootApi,
			data: projectResourcePayload(project.project, { designId }),
		});
		project.onUpdate(response.data);
	});
	const onFlowSelect = async design => {
		if (!draftMode && workspace.workspace?.id && !design.isOwner && !designs.some(item => item.id === design.id)) {
			const response = await store.createRecord({
				url: `${project.rootApi}/resources/designs`,
				data: { source: design.visibility === 'public' ? 'marketplace' : 'workspace', resourceId: design.id },
			});
			project.onUpdate(response.data);
		}
		setDesigns(current =>
			[design, ...current.filter(item => item.id !== design.id)].sort(
				(a, b) => Number(Boolean(b.default)) - Number(Boolean(a.default)),
			),
		);
		if (draftMode || designs.some(item => item.id === design.id) || design.isOwner) await onSelect(design);
	};
	const onFlow = view => {
		setFlowView(view);
		setFlowOpen(true);
	};
	const renderDesign = design => (
		<ComposerActionRow
			key={design.id}
			label={design.name}
			selected={selectedId === design.id}
			disabled={disabled || selectTask.running}
			trailing={
				<span className='flex shrink-0 items-center gap-2'>
					{design.default ? (
						<span className='text-xs font-normal text-foreground-subtle'>Generated</span>
					) : (
						<AgentDesignPalette colors={design.colors} />
					)}
					{selectTask.running && selectTask.id === design.id ? (
						<Spinner className='size-3.5 text-foreground-body' />
					) : (
						<StructuralIcon
							icon={selectedId === design.id ? FarIcons.farCircleCheck : FarIcons.farChevronRight}
							className={selectedId === design.id ? 'size-3.5 text-success' : 'size-3 text-foreground-body'}
							aria-hidden
						/>
					)}
				</span>
			}
			onClick={() => onSelect(design)}
		/>
	);

	return (
		<>
			<ComposerActionHeader onPrev={onPrev}>
				<SearchField
					value={query}
					placeholder='Search designs...'
					className='h-8 grow'
					onChange={event => setQuery(event.target.value)}
					onClear={() => setQuery('')}
				/>
			</ComposerActionHeader>
			<FlowBody>
				{loading ? (
					<ComposerActionListSkeleton count={5} />
				) : error ? (
					<ComposerActionFeedback
						icon='triangle-exclamation'
						title='Designs unavailable'
						text={error?.message || 'Designs could not be loaded.'}
					/>
				) : compact.length ? (
					<ComposerActionSection>{compact.map(renderDesign)}</ComposerActionSection>
				) : (
					<ComposerActionFeedback
						icon='brush'
						title={query ? 'No designs found' : 'No featured designs'}
						text={query ? 'Try a different search.' : 'Featured agent designs will appear here.'}
					/>
				)}
				<ComposerActionSection>
					<ComposerActionRow label='View More' disabled={disabled} onClick={() => onFlow('select')} />
					<ComposerActionRow
						label='Add New'
						disabled={disabled || !canManageResources}
						onClick={() => onFlow('form')}
					/>
				</ComposerActionSection>
			</FlowBody>
			<AgentDesignFlow
				open={flowOpen}
				setOpen={setFlowOpen}
				initialView={flowView}
				selectedId={selectedId}
				onSelect={onFlowSelect}
				canManageResources={canManageResources}
				onCreate={
					draftMode
						? async resource => ({
								...resource,
								id: `pending-design-${Date.now()}`,
								pendingResource: resource,
							})
						: undefined
				}
			/>
		</>
	);
};

export { ComposerActionsDesign };
